import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Users, Globe, User as UserIcon } from "lucide-react";
import FXBackdrop from "../components/FXBackdrop";
import { useAuth } from "../contexts/AuthContext";

const fade = {
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.1 },
    transition: { duration: 0.6 },
};
const inputCls =
    "h-11 rounded-xl border px-3 bg-white/80 text-slate-900 outline-none focus:ring-2 focus:ring-violet-300 w-full";
const inputStyle = { borderColor: "rgba(226,232,240,0.9)" };
const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const COMPANY_TYPES = [
    "SaaS",
    "Fintech",
    "Healthtech",
    "AI / ML",
    "E-commerce",
    "Marketplace",
    "Developer Tools",
    "Cybersecurity",
    "Climate / Energy",
    "EdTech",
    "Other",
];

const SIZE_OPTIONS = [
    "1-10",
    "11-50",
    "51-200",
    "201-500",
    "500+",
];

export default function CompanyOnboarding() {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [form, setForm] = useState({
        company_name: "",
        industry: "",
        company_size: "",
        website: "",
        contact_person: "",
    });

    // Auth gating
    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated) {
            navigate("/login", { replace: true });
            return;
        }
        if (user?.role !== "company") {
            navigate("/", { replace: true });
        }
    }, [authLoading, isAuthenticated, user, navigate]);

    // Fetch existing onboarding state. If already complete, skip directly.
    useEffect(() => {
        async function load() {
            if (!isAuthenticated || user?.role !== "company") return;
            try {
                const token = localStorage.getItem("hirrd_token");
                const res = await fetch(`${API}/api/company/onboarding/status`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.completed) {
                        navigate("/company/matches", { replace: true });
                        return;
                    }
                    setForm((f) => ({
                        ...f,
                        company_name: data.company_name || "",
                        industry: data.industry || "",
                        company_size: data.company_size || "",
                        website: data.website || "",
                        contact_person: data.contact_person || "",
                    }));
                }
            } catch {
                // ignore — user can still fill the form
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [isAuthenticated, user, navigate]);

    function update(field) {
        return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const token = localStorage.getItem("hirrd_token");
            const res = await fetch(`${API}/api/company/onboarding`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Failed to save onboarding");
            navigate("/company/matches", { replace: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    const canSubmit =
        form.company_name.trim() &&
        form.industry &&
        form.company_size &&
        form.contact_person.trim();

    if (authLoading || loading) {
        return (
            <FXBackdrop>
                <section className="containerX pt-20 pb-16 flex justify-center">
                    <div className="animate-pulse text-slate-400">Loading...</div>
                </section>
            </FXBackdrop>
        );
    }

    return (
        <FXBackdrop>
            <section className="containerX pt-10 md:pt-16 pb-16">
                <motion.div {...fade} className="max-w-xl mx-auto">
                    <div className="chip">
                        <span
                            className="h-2 w-2 rounded-full"
                            style={{ background: "rgb(139,92,246)" }}
                        />
                        Company Onboarding
                    </div>
                    <h1 className="h1 mt-5 text-3xl md:text-4xl">Tell us about your company</h1>
                    <p className="p mt-2">
                        A few quick details so we can match you with the right engineers.
                    </p>

                    <form onSubmit={handleSubmit} className="glass p-6 md:p-8 mt-6 grid gap-4">
                        {error && (
                            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                                {error}
                            </div>
                        )}

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block flex items-center gap-1.5">
                                <Building2 size={14} /> Company name *
                            </label>
                            <input
                                className={inputCls}
                                style={inputStyle}
                                value={form.company_name}
                                onChange={update("company_name")}
                                placeholder="Acme Inc."
                                required
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-2 block">
                                Company type *
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {COMPANY_TYPES.map((type) => (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, industry: type }))}
                                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                                            form.industry === type
                                                ? "bg-violet-100 text-violet-700 border border-violet-300"
                                                : "bg-white/60 text-slate-600 border hover:bg-white/90"
                                        }`}
                                        style={form.industry !== type ? inputStyle : undefined}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-1.5">
                                <Users size={14} /> Company size *
                            </label>
                            <div className="grid grid-cols-5 gap-2">
                                {SIZE_OPTIONS.map((size) => (
                                    <button
                                        key={size}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, company_size: size }))}
                                        className={`py-2 px-2 rounded-xl border text-sm font-medium transition ${
                                            form.company_size === size
                                                ? "bg-violet-100 border-violet-300 text-violet-700"
                                                : "bg-white/50 hover:bg-white/80 text-slate-600"
                                        }`}
                                        style={form.company_size !== size ? inputStyle : undefined}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block flex items-center gap-1.5">
                                <UserIcon size={14} /> Primary contact *
                            </label>
                            <input
                                className={inputCls}
                                style={inputStyle}
                                value={form.contact_person}
                                onChange={update("contact_person")}
                                placeholder="Jane Doe, Head of Talent"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700 mb-1 block flex items-center gap-1.5">
                                <Globe size={14} /> Website
                            </label>
                            <input
                                className={inputCls}
                                style={inputStyle}
                                value={form.website}
                                onChange={update("website")}
                                placeholder="https://acme.com"
                                type="url"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={saving || !canSubmit}
                            className="btn btnPrimary justify-center mt-2 disabled:opacity-50"
                        >
                            {saving ? "Saving..." : "Continue"} {!saving && <ArrowRight size={16} />}
                        </button>
                    </form>
                </motion.div>
            </section>
        </FXBackdrop>
    );
}
